/* eslint-disable @typescript-eslint/no-explicit-any */
import { useGalleryCtx } from "@/contexts/gallery";
import { useLocaleCtx } from "@/contexts/locale";
import { getGalleryPageCarpets } from "@/services/gallery/gallery.client";
import { filter } from "../types/common";
import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { CarpetsSectionContext } from "../types/common";
import { useSearchParams } from "next/navigation";
import { YearQuery } from "../common/filter";

const CarpetContext = createContext<CarpetsSectionContext | undefined>(undefined);

export const useCarpets = () => {
  const context = useContext(CarpetContext);

  if (!context) {
    throw new Error("useCarpet must be used within a CarpetProvider");
  }

  return context;
};

export const CarpetProvider = ({ data, children }: { data: any; children: ReactNode }) => {
  const params = useSearchParams();

  const initialYear = {
    title: params.get(YearQuery.CARPETS_YEAR) || data.year_filter_text.years[4],
    value: params.get(YearQuery.CARPETS_YEAR) || "",
  };

  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(6);
  const [year, setYear] = useState<filter>(initialYear);
  const [type, setType] = useState<filter>({
    title: data.dropdown_text.values[2],
    value: "",
  });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [initialDataEnded, setInitialDataEnded] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [usedCarpetsCount, setUsedCarpetsCount] = useState(0);

  const { lang } = useLocaleCtx();
  const { setCarpets, carpets } = useGalleryCtx();

  const appendUniqueCarpets = (newData: any) => {
    setCarpets((prev: any) => {
      const existingIds = prev.map((Carpet: any) => Carpet.id);
      const filteredData = newData.filter((Carpet: any) => !existingIds.includes(Carpet.id));
      return [...prev, ...filteredData];
    });
  };

  const fetchMoreCarpets = async () => {
    setLoadingMore(true);

    const isFiltered = year.value !== "" || type.value !== "";

    if (isFiltered) {
      // If filters are applied, fetch from the API
      const galleryData = await getGalleryPageCarpets({
        lang,
        pageNo,
        pageSize,
        year: year.value,
        type: type.value,
      });

      if (galleryData) {
        if (galleryData.data.length < 6) {
          setReachedEnd(true);
        }
        appendUniqueCarpets(galleryData.data);
      }
    } else {
      // Check if there are remaining Carpets from the initial data
      const remainingCarpets = data.lavender_carpets.slice(usedCarpetsCount, usedCarpetsCount + 6);

      if (remainingCarpets.length > 0) {
        setCarpets((prev: any) => [...prev, ...remainingCarpets]);
        setUsedCarpetsCount((prev) => prev + remainingCarpets.length);
      } else {
        setInitialDataEnded(true);
        setPageNo(1);
      }
    }

    setLoadingMore(false);
  };

  const fetchNewCarpets = async () => {
    setLoading(true);
    setReachedEnd(false);
    setInitialDataEnded(false);

    if (pageNo === 1 && year.value === "" && type.value === "") {
      const initialCarpets = data.lavender_carpets.slice(0, 6);
      setCarpets(initialCarpets);
      setUsedCarpetsCount(initialCarpets.length);
      setLoading(false);

      return;
    }

    const galleryData = await getGalleryPageCarpets({
      lang,
      pageNo: 1,
      pageSize,
      year: year.value,
      type: type.value,
    });

    if (galleryData) {
      setCarpets([...galleryData.data]);
      setUsedCarpetsCount(0);
    }

    setLoading(false);
  };

  useEffect(() => {
    // Once initial data is exhausted, revert to fetching normally

    if (initialDataEnded) {
      (async () => {
        const galleryData = await getGalleryPageCarpets({
          lang,
          pageNo,
          pageSize,
          year: year.value,
          type: type.value,
        });

        if (galleryData) {
          if (galleryData.data.length < 6) {
            setReachedEnd(true);
          }
          appendUniqueCarpets(galleryData.data);
        }
      })();

      return;
    }
    // Else keep fetching to utilize already loaded data
    if (pageNo > 1) {
      fetchMoreCarpets();
    }
  }, [pageNo]);

  useEffect(() => {
    setPageNo(1);
    fetchNewCarpets();
  }, [lang, year, type]);

  const value = {
    pageNo,
    pageSize,
    year,
    type,
    loading,
    loadingMore,
    setPageNo,
    setYear,
    setType,
    setLoading,
    setLoadingMore,
    usedCarpetsCount,
    setUsedCarpetsCount,
    carpets,
    reachedEnd,
  };

  return <CarpetContext.Provider value={value}>{children}</CarpetContext.Provider>;
};
